import { apiClient } from '../utils/api-client';
import type {
  BoardDashboardClimb,
  BoardDashboardClimbDetail,
  BoardDashboardClimbPayload,
  BoardDashboardPage,
  BoardDashboardQuery,
} from '../types/board-dashboard';

interface ApiErrorBody {
  errors?: Array<{ detail: string; code?: string }>;
}

async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  throw new Error(body.errors?.[0]?.detail || fallback);
}

interface ListResponse {
  data: Array<{ id: string; type: string; attributes: BoardDashboardClimb }>;
  meta?: { has_more?: boolean; total?: number; inclinations?: number[] };
}

interface DetailResponse {
  data: { id: string; type: string; attributes: BoardDashboardClimb };
  meta?: Omit<BoardDashboardClimbDetail, 'climb'>;
}

function queryString(params: BoardDashboardQuery): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.append(key, String(value));
  });

  return query.toString() ? `?${query}` : '';
}

/**
 * The gym panel's board dashboard: the problems on one board with the counts a
 * setter judges them by, and one problem's history behind those counts.
 *
 * Staff-only, and deliberately not `devicesService.listBoardClimbs` — that one
 * answers a climber's question ("have I sent this") and knows nothing about a
 * problem's rating average or how often it went up on the wall.
 */
class BoardDashboardService {
  async listClimbs(
    gymSlug: string,
    boardId: string,
    params: BoardDashboardQuery = {}
  ): Promise<BoardDashboardPage> {
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/boards/${boardId}/dashboard_climbs${queryString(params)}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to load the board problems');
    }

    const body: ListResponse = await response.json();

    return {
      climbs: body.data.map((item) => item.attributes),
      hasMore: body.meta?.has_more ?? false,
      total: body.meta?.total ?? body.data.length,
      inclinations: body.meta?.inclinations ?? [],
    };
  }

  /** Everyone who has sent, tried, lit or shared one problem. */
  async getClimbDetail(
    gymSlug: string,
    boardId: string,
    climbId: string
  ): Promise<BoardDashboardClimbDetail> {
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/boards/${boardId}/dashboard_climbs/${climbId}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to load the problem history');
    }

    const body: DetailResponse = await response.json();

    return {
      climb: body.data.attributes,
      ascents: body.meta?.ascents ?? [],
      attempts: body.meta?.attempts ?? [],
      lightings: body.meta?.lightings ?? [],
      shares: body.meta?.shares ?? [],
    };
  }

  /**
   * Corrects a problem the gym set on its own board. The panel is the only
   * place a board climb can be edited — a member sets on a board and lives with
   * it — which is why this hangs off the panel's endpoint and comes back as a
   * dashboard row rather than a climb.
   */
  async updateClimb(
    gymSlug: string,
    boardId: string,
    climbId: string,
    payload: BoardDashboardClimbPayload
  ): Promise<BoardDashboardClimb> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/boards/${boardId}/dashboard_climbs/${climbId}`,
      { data: { attributes: payload } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to save the problem');
    }

    const body: DetailResponse = await response.json();
    return body.data.attributes;
  }

  /** Takes a problem the gym set off its own board. Discarded, not destroyed. */
  async deleteClimb(gymSlug: string, boardId: string, climbId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/gyms/${gymSlug}/boards/${boardId}/dashboard_climbs/${climbId}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the problem');
    }
  }
}

export const boardDashboardService = new BoardDashboardService();
